import { useState, useCallback, useEffect, useRef } from 'react';
import { useReducedMotion } from '@/shared/lib/hooks';

export type Transition = 'idle' | 'exiting' | 'glitching' | 'entering';

export interface UseStepTransitionOptions<S extends string> {
  /** Initial step to display */
  initialStep: S;
  /** Step that triggers auto-navigate (typically 'success') */
  successStep?: S;
  /** Called after success step auto-navigate delay */
  onComplete?: () => void;
  /** Called when step changes — use for focus management & screen reader announcements */
  onStepEntered?: (step: S) => void;
  /**
   * 스텝 진입 시 aria-live 리전으로 나갈 안내 문구 맵.
   * 콘텐츠 스왑(setStep) 시점에 발화 — 포커스 이동(onStepEntered)보다 앞서
   * 안내가 끊기지 않게 한다. 미주입 시 안내 없음(기존 동작).
   */
  announcements?: Partial<Record<S, string>>;
}

/**
 * Generic step transition state machine for auth scenes.
 *
 * Manages: exiting → glitching → entering transition sequence,
 * reduced-motion bypass, and auto-navigate after success.
 */
export function useStepTransition<S extends string>({
  initialStep,
  successStep,
  onComplete,
  onStepEntered,
  announcements,
}: UseStepTransitionOptions<S>) {
  const reducedMotion = useReducedMotion();
  const [step, setStep] = useState<S>(initialStep);
  const [transition, setTransition] = useState<Transition>('idle');
  const [frameGlitch, setFrameGlitch] = useState(false);
  const [stepAnnouncement, setStepAnnouncement] = useState('');
  const [sceneExiting, setSceneExiting] = useState(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const isTransitioning = transition !== 'idle';

  const triggerStepTransition = useCallback(
    (nextStep: S) => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];

      // 콘텐츠 스왑과 같은 시점에 발화 — 포커스 이동(onStepEntered)보다 앞
      const announceStep = () => {
        if (announcements) setStepAnnouncement(announcements[nextStep] ?? '');
      };

      // Reduced-motion: skip decorative animation, just swap content
      if (reducedMotion) {
        setStep(nextStep);
        announceStep();
        timersRef.current.push(
          setTimeout(() => {
            setTransition('idle');
            onStepEntered?.(nextStep);
          }, 50)
        );
        return;
      }

      setTransition('exiting');

      timersRef.current.push(
        setTimeout(() => {
          setTransition('glitching');
          setFrameGlitch(true);
        }, 300)
      );

      timersRef.current.push(
        setTimeout(() => {
          setStep(nextStep);
          announceStep();
          setTransition('entering');
          setFrameGlitch(false);
        }, 500)
      );

      timersRef.current.push(
        setTimeout(() => {
          setTransition('idle');
          onStepEntered?.(nextStep);
        }, 900)
      );
    },
    [onStepEntered, reducedMotion, announcements]
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
    };
  }, []);

  // Auto-navigate after success step: show success → warp exit → navigate
  useEffect(() => {
    if (!successStep || step !== successStep) return;

    const exitStartDelay = 1200;
    const exitDuration = reducedMotion ? 200 : 800;

    const exitTimer = setTimeout(() => setSceneExiting(true), exitStartDelay);
    const navTimer = setTimeout(() => onComplete?.(), exitStartDelay + exitDuration);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(navTimer);
    };
  }, [step, successStep, onComplete, reducedMotion]);

  return {
    step,
    transition,
    frameGlitch,
    isTransitioning,
    stepAnnouncement,
    setStepAnnouncement,
    triggerStepTransition,
    sceneExiting,
  };
}
